import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { useRef, useEffect, useMemo } from 'react';
import { compose } from '@reduxjs/toolkit';
import { connect, useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import TextButton from 'core/buttons/TextButton';
import FormContainer from 'core/forms/FormContainer';
import ActionButton from 'core/buttons/ActionButton';
import FormSelect from 'hook-form-controls/FormSelect';
import FormCheckbox from 'hook-form-controls/FormCheckbox';
import ButtonContainer from 'core/buttons/ButtonContainer';
import FormTextField from 'hook-form-controls/FormTextField';
import FormSelectPartner from 'hook-form-controls/FormSelectPartner';
import ValidationErrorMessages from 'core/errors/ValidationErrorMessages';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import YupPassword from 'utils/YupPassword';
import useQueryParams from 'hooks/useQueryParams';
import { createAccount, updateAccount } from 'app/features/accounts/accountSaga';
import { getRoleDropdown } from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import FormSelectRoleId from 'hook-form-controls/FormSelectRoleId';

YupPassword(Yup);

const USER_TYPES = {
  ADMIN: 'ADMIN',
  PARTNER: 'PARTNER',
};

const createAccountSchema = Yup.object().shape({
  user_type: Yup.string().required('User Type is required'),
  partner_id: Yup.string()
    .nullable()
    .when('user_type', {
      is: USER_TYPES.PARTNER,
      then: Yup.string().required('Partner is required'),
    }),
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  role_ids: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one role')
    .required('Role is required'),
  phone_number: Yup.string().required('Phone number is required'),
  email: Yup.string().email('Enter a valid email address').required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(8, 'Password must contain 8 or more characters')
    .minLowercase(1, 'Password must contain at least 1 lower case letter')
    .minUppercase(1, 'Password must contain at least 1 upper case letter')
    .minNumbers(1, 'Password must contain at least 1 number')
    .minSymbols(1, 'Password must contain at least 1 special character'),
  confirm_password: Yup.string()
    .required('Please retype your password.')
    .oneOf([Yup.ref('password')], 'Your passwords do not match.'),
  is_active: Yup.boolean(),
});

const editAccountSchema = Yup.object().shape({
  first_name: Yup.string().nullable().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  role_ids: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one role')
    .required('Role is required'),
  phone_number: Yup.string().required('Phone number is required'),
});

const editAccountSchemaWithUserType = Yup.object().shape({
  user_type: Yup.string().required('User Type is required'),
  partner_id: Yup.string()
    .nullable()
    .when('user_type', {
      is: USER_TYPES.PARTNER,
      then: Yup.string().required('Partner is required'),
    }),
  first_name: Yup.string().nullable().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  role_ids: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one role')
    .required('Role is required'),
  phone_number: Yup.string().required('Phone number is required'),
});

function AccountForm({ isAddMode, account, retries, isCreated }) {
  const submitButtonRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useQueryParams();

  const showUserTypeAndPartner = !Auth.isPartnerPortalUser();

  const accountValidationSchema = useMemo(() => {
    if (isAddMode) return createAccountSchema;
    return showUserTypeAndPartner ? editAccountSchemaWithUserType : editAccountSchema;
  }, [isAddMode, showUserTypeAndPartner]);

  const { data: roleDropdownData } = useSelector((state) => state.rolesAndPermissions.roleDropdown);

  const roleOptions = useMemo(
    () =>
      (roleDropdownData || []).map((item) => ({
        label: item.role_name,
        value: item.id,
      })),
    [roleDropdownData],
  );

  const roleLabelById = useMemo(() => {
    const map = {};
    (roleDropdownData || []).forEach((item) => {
      if (item?.id != null) map[item.id] = item.role_name;
    });
    return map;
  }, [roleDropdownData]);

  const accountDefaults = useMemo(() => {
    if (!account) return {};
    const { roles, role_ids: accountRoleIds, ...rest } = account;

    const role_ids =
      Array.isArray(accountRoleIds) && accountRoleIds.length
        ? accountRoleIds
        : Array.isArray(roles)
        ? roles.map((r) => (typeof r === 'string' ? r : r?.id)).filter(Boolean)
        : [];

    return { ...rest, user_type: rest.user_type || '', role_ids };
  }, [account]);

  const methods = useForm({
    defaultValues: {
      is_active: true,
      user_type: '',
      role_ids: [],
      ...(!Auth.isSuperAdmin() ? { partner_id: Auth.getPartnerId() } : {}),
      ...(Auth.isPartnerPortalUser() && isAddMode ? { user_type: USER_TYPES.PARTNER } : {}),
      ...(account ? accountDefaults : {}),
    },
    resolver: yupResolver(accountValidationSchema),
    mode: 'onChange',
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
  } = methods;

  const userType = watch('user_type');

  useEffect(() => {
    if (!showUserTypeAndPartner) return;
    if (userType !== USER_TYPES.PARTNER) {
      setValue('partner_id', '');
    }
  }, [userType, showUserTypeAndPartner, setValue]);

  useEffect(() => {
    dispatch(getRoleDropdown());
  }, [dispatch]);

  const onSubmit = async (data) => {
    if (isAddMode) {
      const partner_id = data.user_type === USER_TYPES.PARTNER ? data.partner_id : null;

      dispatch(createAccount({ ...data, partner_id }));
      return;
    }

    const userTypeValue = showUserTypeAndPartner
      ? data.user_type
      : account?.user_type ?? data.user_type;

    const partnerIdValue =
      userTypeValue === USER_TYPES.PARTNER
        ? showUserTypeAndPartner
          ? data.partner_id
          : account?.partner_id ?? Auth.getPartnerId() ?? null
        : null;

    dispatch(
      updateAccount({
        id: account.id,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        role_ids: data.role_ids,
        user_type: userTypeValue,
        partner_id: partnerIdValue,
      }),
    );
  };

  useEffect(() => {
    if (retries > 0) submitButtonRef.current.click();
  }, [retries]);

  useEffect(() => {
    if (isCreated) reset({ partner_id: getValues('partner_id') });
  }, [isCreated]);

  return (
    <FormContainer>
      {app.isDevelopmentMode() && <ValidationErrorMessages errors={errors} />}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={3} columnSpacing={4}>
            {showUserTypeAndPartner && (
              <Grid item sm={12} md={6}>
                <FormSelect
                  name="user_type"
                  label="User Type"
                  error={!!errors?.user_type}
                  helperText={errors?.user_type?.message ?? ''}
                  options={[
                    { label: 'Admin', value: USER_TYPES.ADMIN },
                    { label: 'Partner', value: USER_TYPES.PARTNER },
                  ]}
                />
              </Grid>
            )}

            {showUserTypeAndPartner && userType === USER_TYPES.PARTNER && (
              <Grid item sm={12} md={6}>
                <FormSelectPartner
                  name="partner_id"
                  label="Select Partner"
                  error={!!errors?.partner_id}
                  helperText={errors?.partner_id?.message ?? ''}
                />
              </Grid>
            )}

            <Grid item sm={12} md={6}>
              <FormSelectRoleId options={roleOptions} roleLabelById={roleLabelById} />
            </Grid>

            <Grid item sm={12} md={6}>
              <FormTextField
                name="first_name"
                label="First Name"
                error={!!errors?.first_name}
                helperText={errors?.first_name?.message ?? ''}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormTextField
                name="last_name"
                label="Last Name"
                error={!!errors?.last_name}
                helperText={errors?.last_name?.message ?? ''}
              />
            </Grid>

            <Grid item sm={12} md={6}>
              <FormTextField
                name="phone_number"
                label="Phone Number"
                error={!!errors?.phone_number}
                helperText={errors?.phone_number?.message ?? ''}
              />
            </Grid>
            {isAddMode && (
              <>
                <Grid item xs={12} sm={12} md={6}>
                  <FormTextField
                    name="email"
                    label="Email"
                    error={!!errors?.email}
                    helperText={errors?.email?.message ?? ''}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <FormTextField
                    type="password"
                    name="password"
                    label="Password"
                    error={!!errors?.password}
                    helperText={errors?.password?.message ?? ''}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <FormTextField
                    type="password"
                    name="confirm_password"
                    label="Confirm Password"
                    error={!!errors?.confirm_password}
                    helperText={errors?.confirm_password?.message ?? ''}
                  />
                </Grid>
                <Grid item sm={12} md={6}>
                  <FormCheckbox
                    name="is_active"
                    label="Is Active ?"
                    error={!!errors?.is_active}
                    helperText={errors?.is_active?.message ?? ''}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={12} md={12}>
              <ButtonContainer>
                <TextButton type="button" color="primary" onClick={() => navigate(-1)}>
                  Cancel
                </TextButton>
                <ActionButton
                  ref={submitButtonRef}
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                >
                  {isAddMode ? 'Create' : 'Update'}
                </ActionButton>
              </ButtonContainer>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </FormContainer>
  );
}

export default compose(connect(mapStateToProps))(AccountForm);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    retries: ownProps.isAddMode ? state.accounts.create.retries : state.accounts.update.retries,
    isCreated: state.accounts.create.isCreated,
  };
}

AccountForm.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  account: PropTypes.object,
  retries: PropTypes.number,
  isCreated: PropTypes.bool,
};
